import '@iotize/tap/service/impl/group';

import { isCodeError } from '@iotize/common/error';
import { Tap, TapError, TapResponseStatusError } from '@iotize/tap';
import { ResultCode } from '@iotize/tap/client/api';
import { BehaviorSubject, Observable } from 'rxjs';

import { BasicAuth } from './basic-auth';
import { INITIAL_SESSION_STATE } from './config';
import { debug } from './debug';
import {
  AuthMethod,
  TapLoginEvent,
  TapLogoutEvent,
  TapUserPasswordChangeEvent,
  UsernamePassword,
} from './definitions';
import { ScramAuth } from './scram-auth';
import { TapAuthError } from './tap-auth-error';

const TAG = 'TapAuth';

export namespace TapAuth {
  export type SessionData = ScramAuth.SessionData;
  export interface SessionState {
    /**
     * User identifier
     */
    groupId: number;
    /**
     * Profile identifier
     * If groupId === profileId, it means that user is logged with a profile
     */
    profileId: number;
    startTime?: Date;
    lifeTime: number;
    /**
     * User name
     */
    name: string;
    /**
     * Profile name (may be same as name if user is logged with a profile)
     */
    profileName: string;
  }
}

export class TapAuth
  implements AuthMethod<UsernamePassword, TapAuth.SessionData>
{
  protected _sessionState = new BehaviorSubject<TapAuth.SessionState>(
    INITIAL_SESSION_STATE,
  );
  protected _sessionData = new BehaviorSubject<TapAuth.SessionData>({});

  /**
   * Get current session state snapshot
   */
  public get sessionStateSnapshot() {
    return this._sessionState.value;
  }

  /**
   * Listen to session state changed
   */
  public get sessionState(): Observable<TapAuth.SessionState> {
    return this._sessionState.asObservable();
  }

  /**
   * Get current session data snapshot
   */
  public get sessionDataSnapshot() {
    return this._sessionData.value;
  }

  /**
   * Listen to session data changed
   */
  public get sessionData(): Observable<ScramAuth.SessionData> {
    return this._sessionData.asObservable();
  }

  private _auth?: AuthMethod<UsernamePassword, TapAuth.SessionData>;

  private get service() {
    return this.tap.service;
  }
  constructor(public tap: Tap) {}

  setAuthMethod(method: AuthMethod<UsernamePassword, TapAuth.SessionData>) {
    this._auth = method;
  }

  /**
   * Clear auth cache.
   * Usefull for example if Tap configuration has changed
   */
  public clearCache() {
    this._auth = undefined;
  }
  // async test() {
  //     const lockInfo = await this.getLockInfo();
  //     if (userPassword) {
  //         if (currentUserId === undefined) {
  //             currentUserId = (await this.service.interface.getCurrentGroupId()).body();
  //         }
  //         const currentUsername = (await this.tap.service.group.getName(
  //             currentUserId!
  //         )).body()!;
  //         await this.login(currentUsername, userPassword, false);
  //     }
  // }

  public async login(
    params: UsernamePassword,
    options?: {
      noRefreshSessionState?: boolean;
    },
  ): Promise<TapAuth.SessionData> {
    debug(TAG, `Tap login as ${params.username}...`);
    const auth = await this.setupTapAuthEngineIfRequired();
    try {
      const sessionData = await auth.login(params);
      if (sessionData && sessionData.key) {
        this.tap.encryption.sessionKey = sessionData.key;
        this.tap.encryption.resume();
      }
      this._sessionData.next(sessionData);

      if (!options?.noRefreshSessionState) {
        await this.refreshSessionState({
          username: params.username,
        });
      }
      const event: TapLoginEvent = {
        type: 'tap-login',
      };
      this.tap.notifyEvent(event);

      return sessionData;
    } catch (err) {
      if (
        isCodeError<TapResponseStatusError>(
          TapError.Code.ResponseStatusError,
          err,
        )
      ) {
        if (
          err.response.status === ResultCode.NOT_ACCEPTABLE ||
          err.response.status === ResultCode.UNAUTHORIZED
        ) {
          throw TapAuthError.invalidCredentialsError(params, err);
        } else if (err.response.status === ResultCode.SERVICE_UNAVAILABLE) {
          throw TapAuthError.tooManyLoginFailedError(params, err);
        }
      }
      throw TapAuthError.tapRequestError(params, err as Error);
    }
  }

  /**
   * Tap logout
   * Reject if logout failed
   */
  public async logout(): Promise<void> {
    debug(TAG, `Tap logout...`);
    const auth = await this.setupTapAuthEngineIfRequired();
    await auth.logout();
    // we keep session keys even after logout
    this._sessionState.next(INITIAL_SESSION_STATE);
    this._sessionData.next({});
    const event: TapLogoutEvent = {
      type: 'tap-logout',
    };
    this.tap.notifyEvent(event);
  }

  /**
   * Read session state from the Tap
   * This will perform a few tap requests
   *
   * TODO we should invalidate encryption key if session state changed
   */
  public async refreshSessionState(defaults?: {
    username?: string;
  }): Promise<TapAuth.SessionState> {
    debug(
      TAG,
      '[TAP]',
      `Refreshing session state (current=${this.sessionStateSnapshot.name})...`,
    );
    const [groupIdResponse, profileIdResponse] =
      await this.service.interface.executeMultipleCalls([
        this.service.interface.getCurrentGroupIdCall(),
        this.service.interface.getCurrentProfileIdCall(),
      ]);
    const groupId = groupIdResponse.body();
    const profileId = profileIdResponse.body();
    if (groupId !== this.sessionStateSnapshot.groupId) {
      const currentSessionState = { ...this.sessionStateSnapshot };
      currentSessionState.groupId = groupId;
      currentSessionState.profileId = profileId;
      const [sessionLifetimeResponse, userNameResponse, profileNameResponse] =
        await this.service.interface.executeMultipleCalls([
          this.service.group.getSessionLifetimeCall(groupId),
          this.service.group.getNameCall(groupId),
          this.service.group.getNameCall(profileId),
        ]);
      currentSessionState.name = defaults?.username || userNameResponse.body();
      currentSessionState.profileName = profileNameResponse.body();
      currentSessionState.lifeTime = sessionLifetimeResponse.body();
      debug(
        TAG,
        '[TAP]',
        `Notifying new session state: ${currentSessionState.name}`,
      );
      this._sessionState.next(currentSessionState);
    } else {
      debug(
        TAG,
        '[TAP]',
        `Session state did not changed: ${this.sessionStateSnapshot.name} (id=${groupId})`,
      );
    }
    return this.sessionStateSnapshot;
  }

  // private _isLoggedIn(): boolean {
  //     return (
  //         this.sessionStateSnapshot != undefined &&
  //         this.sessionStateSnapshot.name !==
  //         INITIAL_SESSION_STATE.name
  //     );
  // }

  /**
   * Change password for given user. If userIdOrName is not set, it will change password for the currenty
   * connected user.
   * @param newPassword
   * @param userIdOrUndefined user identifier for which password will be change
   */
  async changePassword(
    newPassword: string,
    userIdOrUndefined?: number,
  ): Promise<void> {
    // When tap will be able to change password by username, we will be able to pass directly the username instead of the userId
    let userId: number;
    // let username: string;
    if (userIdOrUndefined === undefined) {
      userId = (await this.service.interface.getCurrentGroupId()).body();
      // username = (await this.service.group.getName(userId)).body();
    } else {
      // TODO for now we cannot get the user id from the username
      // if (typeof userIdOrName === "string") {
      //     username = userIdOrName;
      //     userId = (await this.service.group.getUserIdFromName(username)).body();
      // }
      // else {
      userId = userIdOrUndefined;
      // username = (await this.service.group.getName(userId)).body();
      // }
    }
    const auth = await this.setupTapAuthEngineIfRequired();
    await auth.changePassword(newPassword, userId);

    const event: TapUserPasswordChangeEvent = {
      type: 'tap-user-password-change',
      user: {
        id: userId,
        // username,
      },
    };
    this.tap.notifyEvent(event);
  }

  async setupTapAuthEngineIfRequired(): Promise<
    AuthMethod<UsernamePassword, TapAuth.SessionData>
  > {
    if (!this._auth) {
      this._auth = await this.setupTapAuthEngine();
    }
    return this._auth;
  }

  private async setupTapAuthEngine() {
    const securityOptions = (
      await this.service.interface.getSecurityOptions()
    ).body();
    debug(TAG, `securityOptions ${JSON.stringify(securityOptions)}`);
    if (securityOptions.scramActivated) {
      debug(TAG, 'Setup with ScramAuth');
      return new ScramAuth(this.tap);
    } else {
      debug(TAG, 'Setup with BasicAuth');
      return new BasicAuth(this.tap, {
        hashPassword: securityOptions.hashPassword,
      });
    }
  }
}
